import { __ } from "@wordpress/i18n";
import type { EnrollmentStatus } from "../store/enrollment-store";

export type Intent = "auto" | "signin" | "signup" | "join";

export type Stage =
	| "emailEntry"
	| "password"
	| "createAccount"
	| "joinExisting"
	| "success";

export type MachineState = {
	stage: Stage;
	email: string;
	prefillFirstName?: string;
	prefillLastName?: string;
	error?: string | null;
	canFlipToSignup?: boolean;
};

export type Event =
	| { type: "emailLookupResult"; exists: boolean }
	| {
			type: "loginSuccess";
			isEnrolled: boolean;
			firstName: string;
			lastName: string;
	  }
	| { type: "loginFailed" }
	| { type: "emailExistsError" }
	| { type: "registerSuccess" }
	| { type: "joinSuccess" }
	| { type: "flipToSignup" }
	| { type: "backToEmail" };

export function initialStageForIntent(
	intent: Intent,
	status: EnrollmentStatus,
	hasPrefillEmail: boolean = false,
): Stage {
	if (intent === "join") return "joinExisting";
	if (intent === "signup") return hasPrefillEmail ? "createAccount" : "emailEntry";
	if (intent === "auto" && status === "loggedInNotEnrolled")
		return "joinExisting";
	return "emailEntry";
}

export function transition(
	state: MachineState,
	event: Event,
	intent: Intent,
): MachineState {
	switch (state.stage) {
		case "emailEntry":
			if (event.type === "emailLookupResult" && event.exists) {
				return {
					...state,
					stage: "password",
					error: null,
					canFlipToSignup: false,
				};
			}
			if (event.type === "emailLookupResult" && !event.exists) {
				if (intent === "signin") {
					return {
						...state,
						stage: "emailEntry",
						error: __(
							"We couldn't find an account with that email.",
							"leat-crm",
						),
						canFlipToSignup: true,
					};
				}
				return {
					...state,
					stage: "createAccount",
					error: null,
					canFlipToSignup: false,
				};
			}
			if (event.type === "flipToSignup") {
				return {
					...state,
					stage: "createAccount",
					error: null,
					canFlipToSignup: false,
				};
			}
			return state;

		case "password":
			if (event.type === "loginSuccess" && event.isEnrolled) {
				return { ...state, stage: "success", error: null };
			}
			if (event.type === "loginSuccess" && !event.isEnrolled) {
				return {
					...state,
					stage: "joinExisting",
					prefillFirstName: event.firstName,
					prefillLastName: event.lastName,
					error: null,
				};
			}
			if (event.type === "backToEmail") {
				return { ...state, stage: "emailEntry", email: "", error: null };
			}
			return state;

		case "createAccount":
			if (event.type === "registerSuccess") {
				return { ...state, stage: "success", error: null };
			}
			if (event.type === "emailExistsError") {
				return { ...state, stage: "password", error: null };
			}
			if (event.type === "backToEmail") {
				return { ...state, stage: "emailEntry", email: "", error: null };
			}
			return state;

		case "joinExisting":
			if (event.type === "joinSuccess") {
				return { ...state, stage: "success", error: null };
			}
			return state;

		case "success":
			return state;

		default:
			return state;
	}
}
